import React, {
  useMemo,
  useEffect,
  useCallback,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { fetchNotes } from "../store/slices/notesSlice";
import { trackPageVisit } from "../utils/analytics";
import { Note } from "../types";
import FilterSortControls, { FilterSortOptions } from "./FilterSortControls";
import AppSidebar from "./AppSidebar";
import { useTheme } from "../hooks/useTheme";

const getInitials = (
  name?: string | null,
  username?: string | null,
): string => {
  if (name)
    return name
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  if (username) return username.charAt(0).toUpperCase();
  return "U";
};

const NoteList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isDark, setTheme } = useTheme();
  const { items: notes, loading } = useAppSelector((state) => state.notes);
  const userProfile = useAppSelector((state) => state.user.profile);
  const hasFetchedRef = useRef(false);
  const initials = getInitials(userProfile?.name, userProfile?.username);

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  // Filter and sort state
  const [filterSortOptions, setFilterSortOptions] = useState<FilterSortOptions>(
    {
      sortBy: "updated-desc",
      filterTag: "",
    },
  );

  useEffect(() => {
    trackPageVisit("note_list", {
      total_notes: notes.length,
    });
  }, [notes]);

  const fetchNotesData = useCallback(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    // Fetch latest notes
    dispatch(fetchNotes());
  }, [dispatch]);

  useEffect(() => {
    fetchNotesData();
  }, [fetchNotesData]);

  // Get all unique tags from notes
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    notes.forEach((note) => {
      if (note.tags) {
        note.tags.forEach((tag) => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [notes]);

  // Filter and sort notes based on current options
  const filteredAndSortedNotes = useMemo(() => {
    let filtered = notes.slice();

    // Apply tag filter
    if (filterSortOptions.filterTag) {
      filtered = filtered.filter(
        (note) => note.tags && note.tags.includes(filterSortOptions.filterTag),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (filterSortOptions.sortBy) {
        case "date-desc":
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        case "date-asc":
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        case "updated-desc":
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        case "updated-asc":
          return (
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
          );
        case "title-asc":
          return a.title.localeCompare(b.title);
        case "title-desc":
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

    return filtered;
  }, [notes, filterSortOptions]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleBack = () => {
    navigate("/app");
  };

  const handleViewNote = (noteId: string) => {
    navigate(`/note/${noteId}`);
  };

  const handleAddNote = () => {
    navigate("/add-note");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#22c67a] rounded-2xl mb-6">
            <svg
              className="animate-spin w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t("noteList.loadingNotes")}
          </h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title={t("noteList.title")}
        description={t("noteList.description") as string}
        keywords={t("noteList.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#22c67a]/20 border border-[#22c67a]/30 flex items-center justify-center transition-all hover:bg-[#22c67a]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#22c67a"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("noteList.headerTitle")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#22c67a]/15 border border-[#22c67a]/25 flex items-center justify-center transition-all hover:bg-[#22c67a]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#22c67a"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("noteList.headerTitle")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-5xl mx-auto pb-8">
              {/* Stats Header */}
              {filteredAndSortedNotes.length > 0 && (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-6">
                  {/* Total Notes */}
                  <div className="glass-card p-4 lg:p-5 rounded-xl">
                    <div className="text-[15px] lg:text-[16px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                      {t("noteList.totalNotes")}
                    </div>
                    <div className="text-2xl lg:text-3xl font-semibold text-neutral-900 dark:text-neutral-100">
                      {notes.length}
                    </div>
                  </div>

                  {/* Public Notes */}
                  <div className="glass-card p-4 lg:p-5 rounded-xl">
                    <div className="text-[15px] lg:text-[16px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                      {t("noteList.publicNotesStat")}
                    </div>
                    <div className="text-2xl lg:text-3xl font-semibold text-[#22c67a]">
                      {notes.filter((n) => n.visibility === "public").length}
                    </div>
                  </div>

                  {/* Private Notes */}
                  {notes.some((n) => n.visibility === "private") && (
                    <div className="col-span-2 lg:col-span-1 glass-card p-4 lg:p-5 rounded-xl">
                      <div className="text-[15px] lg:text-[16px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                        {t("noteList.privateNotesStat")}
                      </div>
                      <div className="text-2xl lg:text-3xl font-semibold text-neutral-900 dark:text-neutral-100">
                        {notes.filter((n) => n.visibility === "private").length}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Filter and Sort Controls */}
              <div className="mb-4">
                <FilterSortControls
                  options={filterSortOptions}
                  onOptionsChange={setFilterSortOptions}
                  availableTags={availableTags}
                />
              </div>

              <hr className="mt-3 mb-3 mx-4 content-center opacity-30" />

              {/* Note Grid */}
              {filteredAndSortedNotes.length === 0 ? (
                <div className="glass-card p-12 lg:p-16 text-center rounded-xl">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#22c67a]/10 to-[#22c67a]/5 dark:from-[#22c67a]/20 dark:to-[#22c67a]/10 flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-[#22c67a]/40"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                    {t("noteList.noNotes")}
                  </h3>
                  <p className="text-[18px] text-neutral-600 dark:text-neutral-400 mb-6">
                    {t("noteList.noNotesMessage")}
                  </p>
                  <button
                    onClick={handleAddNote}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#22c67a] hover:bg-[#22c67a]/90 text-white rounded-xl font-medium text-[17px] transition-colors duration-150"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    {t("noteList.createFirstNote")}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 my-4">
                  {filteredAndSortedNotes.map((note: Note) => (
                    <div
                      key={note.id}
                      className="glass-card p-4 lg:p-5 rounded-xl hover:bg-black/[0.02] dark:hover:bg-white/[0.08] transition-all duration-150 group flex flex-col cursor-pointer"
                      onClick={() => handleViewNote(note.id)}
                    >
                      {/* Header with Title and Visibility */}
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-[19px] lg:text-base font-semibold text-neutral-900 dark:text-neutral-100 line-clamp-2 flex-1 pr-2">
                          {note.title}
                        </h3>
                        <span
                          className={`px-2.5 py-1.5 text-[14px] font-medium rounded-full whitespace-nowrap flex-shrink-0 ${
                            note.visibility === "public"
                              ? "bg-[#22c67a]/15 text-[#22c67a] dark:bg-[#22c67a]/20"
                              : "bg-neutral-200/50 dark:bg-neutral-700/30 text-neutral-600 dark:text-neutral-400"
                          }`}
                        >
                          {note.visibility === "public"
                            ? t("noteList.publicNote")
                            : t("noteList.privateNote")}
                        </span>
                      </div>

                      {/* Content Preview */}
                      {note.content && (
                        <p className="text-[17px] text-neutral-600 dark:text-neutral-400 line-clamp-3 leading-relaxed mb-4 flex-1">
                          {note.content}
                        </p>
                      )}

                      {/* Tags and Date Footer */}
                      <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
                        {note.tags && note.tags.length > 0 && (
                          <div className="flex items-center gap-1.5 flex-wrap mb-2">
                            {note.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="text-[14px] font-medium px-2 py-1 rounded-full bg-[#22c67a]/15 text-[#22c67a] dark:bg-[#22c67a]/20"
                              >
                                {tag}
                              </span>
                            ))}
                            {note.tags.length > 3 && (
                              <span className="text-[14px] font-medium px-2 py-1 text-neutral-500 dark:text-neutral-400">
                                +{note.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                        <span className="text-[15px] text-neutral-500 dark:text-neutral-400">
                          {formatDate(note.createdAt)}
                        </span>
                      </div>

                      {/* Arrow Icon Indicator */}
                      <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center gap-2 text-[#22c67a] text-[16px] font-medium">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                        View
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Floating Add Note Button */}
        <button
          onClick={handleAddNote}
          className="fixed bottom-24 right-8 z-40 bg-[#22c67a] hover:opacity-90 text-white p-4 rounded-full border border-[#22c67a] transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#22c67a] focus:ring-offset-2"
          aria-label={t("noteList.addNoteAriaLabel") as string}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
        </button>

        {/* MOBILE BOTTOM NAV */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className="text-[22px]">{emoji}</span>
                  <span
                    className={`text-[13px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default NoteList;
